// =============================================================================
// features/configuracoes/comercial/types.ts — Draft de edição da configuração
// comercial + validação client-side + diff para PATCH parcial.
//
// Janela inválida é bloqueada AQUI, antes de qualquer chamada de rede (DoD:
// "Janela inválida bloqueada na UI com mensagem acionável, não só no
// backend"). As mesmas regras do Zod compartilhado
// (packages/shared-schemas/src/sales-settings.ts) são replicadas em forma de
// mensagens de campo — o backend continua sendo a validação de verdade.
// =============================================================================

import type {
  AssignmentStrategy,
  ContactWindow,
  SalesSettings,
  SalesSettingsPatchBody,
} from '@elemento/shared-schemas';

import { FALLBACK_ORG_TIMEZONE } from '../../../lib/orgDateTime';

import { isValidTimeZone } from './contactWindowMath';

export interface SettingsDraft {
  timezone: string;
  contactWindow: ContactWindow;
  maxAttempts: number;
  minIntervalMinutes: number;
  enabledAssignmentStrategies: AssignmentStrategy[];
  cancellationReasons: string[];
  suppressionReasons: string[];
  distributorDryRun: boolean;
}

export function draftFromSettings(settings: SalesSettings): SettingsDraft {
  return {
    timezone: settings.timezone,
    contactWindow: { ...settings.contactWindow, days: [...settings.contactWindow.days] },
    maxAttempts: settings.maxAttempts,
    minIntervalMinutes: settings.minIntervalMinutes,
    enabledAssignmentStrategies: [...settings.enabledAssignmentStrategies],
    cancellationReasons: [...settings.cancellationReasons],
    suppressionReasons: [...settings.suppressionReasons],
    distributorDryRun: settings.distributorDryRun,
  };
}

export interface DraftErrors {
  timezone?: string;
  days?: string;
  startTime?: string;
  endTime?: string;
  maxAttempts?: string;
  minIntervalMinutes?: string;
  enabledAssignmentStrategies?: string;
}

/** Validação client-side — espelha os limites do Zod compartilhado. */
export function validateDraft(draft: SettingsDraft): DraftErrors {
  const errors: DraftErrors = {};

  if (!isValidTimeZone(draft.timezone)) {
    errors.timezone = `Fuso horário inválido — informe um identificador IANA (ex: ${FALLBACK_ORG_TIMEZONE})`;
  }

  if (draft.contactWindow.days.length === 0) {
    errors.days = 'Selecione ao menos um dia da semana permitido';
  }

  if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(draft.contactWindow.startTime)) {
    errors.startTime = 'Horário inválido';
  }
  if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(draft.contactWindow.endTime)) {
    errors.endTime = 'Horário inválido';
  }
  if (
    !errors.startTime &&
    !errors.endTime &&
    draft.contactWindow.startTime >= draft.contactWindow.endTime
  ) {
    errors.endTime = 'O horário final deve ser depois do horário inicial';
  }

  if (draft.maxAttempts < 1 || draft.maxAttempts > 30) {
    errors.maxAttempts = 'Informe um valor entre 1 e 30';
  }

  if (draft.minIntervalMinutes < 0 || draft.minIntervalMinutes > 10080) {
    errors.minIntervalMinutes = 'Informe um valor entre 0 e 10080 minutos (7 dias)';
  }

  if (draft.enabledAssignmentStrategies.length === 0) {
    errors.enabledAssignmentStrategies = 'Habilite ao menos uma estratégia de distribuição';
  }

  return errors;
}

export function hasErrors(errors: DraftErrors): boolean {
  return Object.values(errors).some((v) => v !== undefined);
}

/** Diff — PATCH só envia campos que realmente mudaram (nunca o objeto inteiro). */
export function diffDraft(current: SalesSettings, draft: SettingsDraft): SalesSettingsPatchBody {
  const patch: SalesSettingsPatchBody = {};

  if (draft.timezone !== current.timezone) patch.timezone = draft.timezone;

  const windowChanged =
    draft.contactWindow.startTime !== current.contactWindow.startTime ||
    draft.contactWindow.endTime !== current.contactWindow.endTime ||
    draft.contactWindow.days.length !== current.contactWindow.days.length ||
    draft.contactWindow.days.some((d) => !current.contactWindow.days.includes(d));
  if (windowChanged) patch.contactWindow = draft.contactWindow;

  if (draft.maxAttempts !== current.maxAttempts) patch.maxAttempts = draft.maxAttempts;
  if (draft.minIntervalMinutes !== current.minIntervalMinutes) {
    patch.minIntervalMinutes = draft.minIntervalMinutes;
  }

  const strategiesChanged =
    draft.enabledAssignmentStrategies.length !== current.enabledAssignmentStrategies.length ||
    draft.enabledAssignmentStrategies.some((s) => !current.enabledAssignmentStrategies.includes(s));
  if (strategiesChanged) patch.enabledAssignmentStrategies = draft.enabledAssignmentStrategies;

  const cancellationChanged =
    draft.cancellationReasons.length !== current.cancellationReasons.length ||
    draft.cancellationReasons.some((r, i) => r !== current.cancellationReasons[i]);
  if (cancellationChanged) patch.cancellationReasons = draft.cancellationReasons;

  const suppressionChanged =
    draft.suppressionReasons.length !== current.suppressionReasons.length ||
    draft.suppressionReasons.some((r, i) => r !== current.suppressionReasons[i]);
  if (suppressionChanged) patch.suppressionReasons = draft.suppressionReasons;

  if (draft.distributorDryRun !== current.distributorDryRun) {
    patch.distributorDryRun = draft.distributorDryRun;
  }

  return patch;
}

export function isDirty(current: SalesSettings, draft: SettingsDraft): boolean {
  return Object.keys(diffDraft(current, draft)).length > 0;
}

/** Rótulos legíveis dos campos alterados — usado no diálogo de impacto. */
export function describeChanges(patch: SalesSettingsPatchBody): string[] {
  const labels: string[] = [];
  if (patch.timezone !== undefined) labels.push('Fuso horário');
  if (patch.contactWindow !== undefined) labels.push('Janela de contato');
  if (patch.maxAttempts !== undefined) labels.push('Tentativas máximas');
  if (patch.minIntervalMinutes !== undefined) labels.push('Intervalo mínimo entre tentativas');
  if (patch.enabledAssignmentStrategies !== undefined) labels.push('Estratégias de distribuição');
  if (patch.cancellationReasons !== undefined) labels.push('Motivos de cancelamento');
  if (patch.suppressionReasons !== undefined) labels.push('Motivos de supressão');
  if (patch.distributorDryRun !== undefined) labels.push('Modo de simulação da distribuição');
  return labels;
}
